import { useState } from 'react';
import { Priority, TicketStatus, Location, JobTag, TAG_CATEGORIES, TicketType } from '../../types/ticket';
import type { Tech } from '../../types/ticket';
import type { AllTicketsFilters } from './AllTicketsView';

interface TicketFiltersProps {
  filters: AllTicketsFilters;
  onFiltersChange: (filters: AllTicketsFilters) => void;
  onClearFilters: () => void;
  techs: Tech[];
  activeFilterCount: number;
}

export const TicketFilters: React.FC<TicketFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  techs,
  activeFilterCount,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, searchQuery: e.target.value });
  };

  const handlePriorityToggle = (priority: Priority) => {
    const newPriorities = filters.priorities.includes(priority)
      ? filters.priorities.filter(p => p !== priority)
      : [...filters.priorities, priority];
    onFiltersChange({ ...filters, priorities: newPriorities });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onFiltersChange({ ...filters, statuses: [] });
    } else {
      onFiltersChange({ ...filters, statuses: [value as TicketStatus] });
    }
  };

  const handleTechChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onFiltersChange({ ...filters, techIds: [] });
    } else if (value === 'unassigned') {
      onFiltersChange({ ...filters, techIds: ['unassigned'] });
    } else {
      onFiltersChange({ ...filters, techIds: [value] });
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onFiltersChange({ ...filters, locations: [] });
    } else {
      onFiltersChange({ ...filters, locations: [value as Location] });
    }
  };

  const handleSlaStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({
      ...filters,
      slaStatus: e.target.value as AllTicketsFilters['slaStatus'],
    });
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({
      ...filters,
      dateRange: e.target.value as AllTicketsFilters['dateRange'],
    });
  };

  const handleTagToggle = (tag: JobTag) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ ...filters, tags: newTags });
  };

  const handleTicketTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onFiltersChange({ ...filters, ticketTypes: [] });
    } else {
      onFiltersChange({ ...filters, ticketTypes: [value as (typeof TicketType)[keyof typeof TicketType]] });
    }
  };

  const t2Techs = techs.filter(t => t.tier === 'T2');

  return (
    <div className="ticket-filters">
      {/* Quick filters row */}
      <div className="ticket-filters__quick">
        {/* Search */}
        <div className="ticket-filters__search">
          <svg
            className="ticket-filters__search-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="7" cy="7" r="5" strokeWidth="1.5" />
            <path d="M11 11l3 3" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className="ticket-filters__search-input"
            placeholder="Search tickets..."
            value={filters.searchQuery}
            onChange={handleSearchChange}
          />
          {filters.searchQuery && (
            <button
              className="ticket-filters__search-clear"
              onClick={() => onFiltersChange({ ...filters, searchQuery: '' })}
              title="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        {/* Priority toggles */}
        <div className="ticket-filters__priorities">
          {Object.values(Priority).map(priority => (
            <button
              key={priority}
              className={`ticket-filters__priority ticket-filters__priority--${priority.toLowerCase()} ${
                filters.priorities.includes(priority) ? 'ticket-filters__priority--active' : ''
              }`}
              onClick={() => handlePriorityToggle(priority)}
            >
              {priority}
            </button>
          ))}
        </div>

        {/* Status dropdown */}
        <select
          className="ticket-filters__select"
          value={filters.statuses[0] || ''}
          onChange={handleStatusChange}
        >
          <option value="">All Statuses</option>
          {Object.values(TicketStatus).map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>

        {/* Ticket Type dropdown */}
        <select
          className="ticket-filters__select"
          value={filters.ticketTypes[0] || ''}
          onChange={handleTicketTypeChange}
        >
          <option value="">All Types</option>
          {Object.values(TicketType).map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        {/* Tech dropdown */}
        <select
          className="ticket-filters__select"
          value={filters.techIds[0] || ''}
          onChange={handleTechChange}
        >
          <option value="">All Techs</option>
          <option value="unassigned">Unassigned</option>
          {t2Techs.map(tech => (
            <option key={tech.id} value={tech.id}>{tech.name}</option>
          ))}
        </select>

        {/* Expand/collapse advanced */}
        <button
          className={`ticket-filters__expand-btn ${isExpanded ? 'ticket-filters__expand-btn--active' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2 4h12M4 8h8M6 12h4" strokeLinecap="round" />
          </svg>
          More Filters
          {activeFilterCount > 0 && (
            <span className="ticket-filters__badge">{activeFilterCount}</span>
          )}
          <svg
            className={`ticket-filters__chevron ${isExpanded ? 'ticket-filters__chevron--open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Clear filters */}
        {activeFilterCount > 0 && (
          <button className="ticket-filters__clear-btn" onClick={onClearFilters}>
            Clear all
          </button>
        )}
      </div>

      {/* Advanced filters row */}
      {isExpanded && (
        <div className="ticket-filters__advanced">
          {/* Location */}
          <div className="ticket-filters__group">
            <label className="ticket-filters__label">Location</label>
            <select
              className="ticket-filters__select"
              value={filters.locations[0] || ''}
              onChange={handleLocationChange}
            >
              <option value="">All Locations</option>
              {Object.values(Location).map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          {/* SLA Status */}
          <div className="ticket-filters__group">
            <label className="ticket-filters__label">SLA Status</label>
            <select
              className="ticket-filters__select"
              value={filters.slaStatus}
              onChange={handleSlaStatusChange}
            >
              <option value="all">All</option>
              <option value="ok">OK</option>
              <option value="at-risk">At Risk (&lt;1hr)</option>
              <option value="breached">Breached</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="ticket-filters__group">
            <label className="ticket-filters__label">Created</label>
            <select
              className="ticket-filters__select"
              value={filters.dateRange}
              onChange={handleDateRangeChange}
            >
              <option value="all">Any time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
            </select>
          </div>

          {/* Tags */}
          <div className="ticket-filters__group ticket-filters__group--tags">
            <label className="ticket-filters__label">Tags</label>
            <div className="ticket-filters__tags-container">
              <button
                className={`ticket-filters__tags-btn ${showTagsDropdown ? 'ticket-filters__tags-btn--active' : ''}`}
                onClick={() => setShowTagsDropdown(!showTagsDropdown)}
              >
                {filters.tags.length > 0 ? (
                  <span>{filters.tags.length} selected</span>
                ) : (
                  <span>Select tags...</span>
                )}
                <svg
                  className={`ticket-filters__chevron ${showTagsDropdown ? 'ticket-filters__chevron--open' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {showTagsDropdown && (
                <div className="ticket-filters__tags-dropdown">
                  {TAG_CATEGORIES.map(category => (
                    <div key={category.id} className="ticket-filters__tag-category">
                      <span className="ticket-filters__tag-category-label">{category.label}</span>
                      <div className="ticket-filters__tag-list">
                        {category.tags.map(tag => (
                          <button
                            key={tag}
                            className={`ticket-filters__tag ${
                              filters.tags.includes(tag) ? 'ticket-filters__tag--active' : ''
                            }`}
                            onClick={() => handleTagToggle(tag)}
                          >
                            {tag}
                            {filters.tags.includes(tag) && (
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 8l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {(filters.tags.length > 0 || filters.locations.length > 0 || filters.ticketTypes.length > 0) && (
        <div className="ticket-filters__active-chips">
          {filters.ticketTypes.map(type => (
            <span key={type} className="ticket-filters__chip ticket-filters__chip--type">
              {type}
              <button
                onClick={() => onFiltersChange({
                  ...filters,
                  ticketTypes: filters.ticketTypes.filter(t => t !== type),
                })}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
          {filters.locations.map(location => (
            <span key={location} className="ticket-filters__chip">
              {location}
              <button
                onClick={() => onFiltersChange({
                  ...filters,
                  locations: filters.locations.filter(l => l !== location),
                })}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
          {filters.tags.map(tag => (
            <span key={tag} className="ticket-filters__chip">
              {tag}
              <button onClick={() => handleTagToggle(tag)}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
